import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { api } from "@/lib/api";
import { FranceMap, metricColor } from "@/components/france-map";
import { Header } from "@/components/header";
import { Badge, Card, ChipRow, ErrorView, Loading } from "@/components/kit";
import { C } from "@/lib/format";

const NEED = {
  critical: { label: "Recruter en priorité", color: C.error },
  high: { label: "Renfort conseillé", color: C.warning },
  ok: { label: "Couverte", color: C.success },
  none: { label: "Pas encore d'activité", color: C.muted },
};
const METRICS = [{ key: "projects", label: "Projets" }, { key: "agents", label: "Agents" }, { key: "masters", label: "Maîtres d'œuvre" }];
const VIEWS = [["regions", "Régions"], ["departments", "Départements"], ["masters", "Maîtres d'œuvre"]];

const Counts = ({ p, a, m }) => <p className="t-caption">{p} projet(s) · {a} agent(s) · {m} maître(s) d'œuvre</p>;

function SelectedDept({ d, metric }) {
  const navigate = useNavigate();
  if (!d) return <p className="t-caption">Cliquez sur un point de la carte pour voir le détail du département.</p>;
  return (
    <Card testID="geo-selected-dept" className="flex flex-col gap-1" style={{ borderColor: metricColor(metric) }}>
      <h3 className="t-h3">{d.code} · {d.name}</h3>
      <p className="t-caption">{d.region}</p>
      <Counts p={d.projects} a={d.agents} m={d.masters} />
      <p className="t-caption">{d.signed} contrat(s) signé(s) · {d.masters_based} maître(s) d'œuvre basé(s) ici</p>
      {d.masters === 0 && (d.projects > 0 || d.agents > 0) ? <Badge label="Aucun maître d'œuvre : à recruter" color={C.error} /> : null}
      <button type="button" data-testid="geo-find-masters" onClick={() => navigate(`/admin-agents?seg=masters&dept=${d.code}`)} className="focus-ring mt-1 w-fit rounded text-sm text-[#D4AF37] hover:underline">
        Voir les maîtres d'œuvre →
      </button>
    </Card>
  );
}

function Lists({ g, view, setView, onDept }) {
  const navigate = useNavigate();
  const active = g.departments.filter((d) => d.projects || d.agents || d.masters);
  return (
    <div className="flex flex-col gap-3">
      <div role="tablist" className="flex rounded-xl border border-[#333333] bg-[#1E1E1E] p-1">
        {VIEWS.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={view === k} data-testid={`geo-view-${k}`} onClick={() => setView(k)}
            className={`focus-ring h-10 flex-1 truncate rounded-lg px-2 text-sm font-medium ${view === k ? "bg-[#292415] text-[#D4AF37]" : "text-[#CCCCCC] hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>
      {view === "regions" && g.regions.map((r) => (
        <Card key={r.name} testID={`geo-region-${r.name}`} className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h3 className="t-h3 min-w-0 flex-1 truncate">{r.name}</h3>
            <Badge label={NEED[r.need].label} color={NEED[r.need].color} />
          </div>
          <Counts p={r.projects} a={r.agents} m={r.masters} />
        </Card>
      ))}
      {view === "departments" && (active.length ? active.map((d) => (
        <button key={d.code} type="button" data-testid={`geo-dept-${d.code}`} onClick={() => onDept(d.code)} className="pressable card flex flex-col gap-1 text-left">
          <span className="t-h3">{d.code} · {d.name}</span>
          <Counts p={d.projects} a={d.agents} m={d.masters} />
        </button>
      )) : <p className="t-caption">Aucune activité pour l'instant.</p>)}
      {view === "masters" && (g.masters.length ? g.masters.map((m) => (
        <button key={m.id} type="button" data-testid={`geo-master-${m.id}`} onClick={() => navigate(`/admin/master/${m.id}`)} className="pressable card flex items-center gap-3 text-left">
          <span className="min-w-0 flex-1">
            <span className="t-h3 block truncate">{m.company_name}</span>
            <span className="t-caption block">{m.city} · {m.signed} signé(s)</span>
          </span>
          <span className="t-h2 !text-[#D4AF37]">{m.projects}</span>
          <ChevronRight className="h-5 w-5 text-[#9E9E9E]" aria-hidden />
        </button>
      )) : <p className="t-caption">Aucun maître d'œuvre actif.</p>)}
    </div>
  );
}

export default function AdminStats() {
  const q = useQuery({ queryKey: ["admin-geo"], queryFn: () => api("/admin/geo") });
  const [metric, setMetric] = useState("projects");
  const [sel, setSel] = useState(null);
  const [view, setView] = useState("regions");
  const g = q.data;
  const selDept = g?.departments.find((d) => d.code === sel);
  return (
    <div data-testid="admin-stats-screen" className="flex flex-col">
      <Header title="Carte & répartition" subtitle="Projets, agents et maîtres d'œuvre" />
      <div className="border-b border-[#222222] px-4 md:px-8">
        <ChipRow options={METRICS} value={metric} onChange={setMetric} testIDPrefix="geo-metric" />
      </div>
      {q.isLoading ? <Loading /> : q.isError || !g ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto grid w-full max-w-6xl items-start gap-6 p-4 pb-8 md:p-8 lg:grid-cols-[minmax(0,6fr)_minmax(0,5fr)]">
          <div className="flex flex-col gap-4 lg:sticky lg:top-6">
            <div className="grid grid-cols-3 gap-3">
              {[["projects", g.totals.projects, "Projets"], ["agents", g.totals.agents, "Agents"], ["masters", g.totals.masters, "Maîtres d'œuvre"]].map(([k, v, l]) => (
                <div key={k} data-testid={`geo-total-${k}`} className="rounded-xl border bg-[#1E1E1E] p-3" style={{ borderColor: metric === k ? metricColor(k) : C.border }}>
                  <p className="t-number" style={{ color: metricColor(k) }}>{v}</p>
                  <p className="t-caption truncate">{l}</p>
                </div>
              ))}
            </div>
            <FranceMap depts={g.departments} metric={metric} selected={sel} onSelect={setSel} />
            <SelectedDept d={selDept} metric={metric} />
            {(g.unlocated.projects > 0 || g.unlocated.agents > 0) && (
              <p className="t-caption" data-testid="geo-unlocated">Non localisés : {g.unlocated.projects} projet(s), {g.unlocated.agents} agent(s) (code postal ou département non reconnu).</p>
            )}
          </div>
          <Lists g={g} view={view} setView={setView} onDept={(c) => { setSel(c); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        </div>
      )}
    </div>
  );
}
